import classNames from 'classnames';
import * as React from 'react';
import { ViewportList } from 'react-viewport-list';
import { v4 as uuid } from 'uuid';

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { SelectOption } from '../../../@types/shared';
import Checkbox from '../../atoms/Checkbox';
import Overlay from '../../atoms/Overlay';
import Spinner from '../../atoms/Spinner';
import Text from '../../atoms/Text';
import SearchBar from '../../molecules/SearchBar';

import { CustomTooltipInfo, Icon } from '../../atoms';
import SelectItem from './SelectItem';
import styles from './Select.module.scss';

interface MultipleSelectProps {
  multiple?: false;
  value?: SelectOption['id'];
  onChange: (value: string) => void;
  options: SelectOption[];
}

interface SingleSelectProps {
  multiple: true;
  value?: Array<SelectOption['id']>;
  onChange: (value: string[]) => void;
  options: SelectOption[];
}

export type Props = (MultipleSelectProps | SingleSelectProps) &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'onChange'> & {
    size?: 'md' | 'sm';
    loading?: boolean;
    onOpen?: () => void;
    disabled?: boolean;
    search?: string;
    withSearch?: boolean;
    onSearch?: (search: string) => void;
    searchPlaceholder?: string;
    label?: string;
    tooltip?: string;
    description?: string;
    helperText?: string;
    errorText?: string;
    prepend?: React.ReactNode;
    append?: React.ReactNode;
  };

const parseValue = (value: Props['value']) => {
  if (Array.isArray(value) && value.length === 0) {
    return [];
  }
  return value;
};

/**
 * <Select> component
 */
export const Select = ({
  id,
  name,
  className,
  style,
  loading,
  value,
  label,
  tooltip,
  size,
  search: controlledSearch,
  searchPlaceholder = 'Pesquisar',
  options,
  description,
  helperText,
  errorText,
  onOpen,
  onChange,
  onSearch,
  multiple = false,
  disabled = false,
  withSearch = false,
  append,
  prepend,
  ...inputProps
}: React.PropsWithChildren<Props>) => {
  const [show, setShow] = React.useState(false);
  const [target, setTarget] = React.useState<HTMLDivElement | null>(null);
  const [selected, setSelected] = React.useState(parseValue(value));
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [focusedOptionIndex, setFocusedOptionIndex] = React.useState(-1);
  const [overlayPlacement, setOverlayPlacement] = React.useState<
    string | undefined
  >('bottom-start');

  const [search, setSearch] = React.useState('');
  const searchBarRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (searchBarRef.current && show) {
      searchBarRef.current.focus();
    }
  });

  // Sync internal state with external state
  React.useEffect(() => {
    setSelected(parseValue(value));
  }, [value]);

  const filteredOptions = React.useMemo(
    () =>
      options.filter(
        (option) =>
          option.label
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) || option.id === '',
      ),
    [options, search],
  );

  const inputId = id ?? uuid();

  // Get value to display
  const displayValue = React.useMemo(() => {
    const parsedValue = parseValue(value);
    if (Array.isArray(parsedValue)) {
      return options
        .filter((opt) => parsedValue.includes(opt.id))
        .map((opt) => opt.label)
        .join(', ');
    }
    return options.find((opt) => opt.id === parsedValue)?.label;
  }, [value, options]);

  /**
   * Handle change to selection when popover is open
   */
  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checkSelections = selected as string[];

      // Reset selection if:
      //  - default (key: '') is selected
      //  - no options is selected
      if (
        event.target.value === '' ||
        (!event.target.checked && checkSelections.length === 1)
      ) {
        setSelected([]);
        return;
      }
      setSelected(
        event.target.checked
          ? [...checkSelections.filter((e) => e !== ''), event.target.value]
          : checkSelections.filter((e) => e !== event.target.value),
      );
    },
    [selected],
  );

  /**
   * Handle change to selection when popover is open
   */
  const handleSelect = React.useCallback(
    (v: string | string[]) => {
      setSelected(v);
      setShow(false);
      if (multiple) {
        onChange(v as string & string[]);
      } else {
        onChange(v as string & string[]);
      }
    },
    [multiple, onChange],
  );

  /**
   * Notify changes when overlay is closed
   */
  const handleOverlayHide = React.useCallback(() => {
    if (multiple) {
      onChange(selected as string & string[]);
    } else {
      onChange(selected as string & string[]);
    }
  }, [onChange, multiple, selected]);

  /** Input props */

  const handleOnBlur: React.FocusEventHandler<HTMLInputElement> =
    React.useCallback(
      (evt) => {
        const modifiedEvent = { ...evt };
        modifiedEvent.target.value = modifiedEvent.target.value?.trim();
        inputProps.onBlur?.(modifiedEvent);
      },
      [inputProps],
    );

  const handleOpen = React.useCallback(() => {
    if (disabled) return;
    // If shown, hide and save filters
    if (show) handleOverlayHide();

    // Toggle show
    setShow((s) => !s);
    if (!show && onOpen) onOpen();
  }, [disabled, handleOverlayHide, onOpen, show]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const { key } = event;
      const totalButtons = filteredOptions.length;

      setShow(true);
      const listBox = document.querySelector('[role="listbox"]');
      if (!listBox) {
        return;
      }

      let index = 0;
      let option;

      switch (key) {
        case 'ArrowUp':
          event.preventDefault();
          index = (focusedOptionIndex + totalButtons - 1) % totalButtons;
          option = listBox.querySelector(`[data-option-index="${index}"]`);

          option?.scrollIntoView({
            block: 'nearest',
          });
          setFocusedOptionIndex(index);
          break;
        case 'ArrowDown':
          event.preventDefault();
          index = (focusedOptionIndex + 1) % totalButtons;
          option = listBox.querySelector(`[data-option-index="${index}"]`);
          option?.scrollIntoView({
            block: 'nearest',
          });
          setFocusedOptionIndex(index);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!(focusedOptionIndex >= 0)) {
            break;
          }
          if (!multiple) {
            handleSelect(filteredOptions[focusedOptionIndex].id);
          } else {
            const selectedArray = selected as string[];
            /** Add or remove the new selected option */
            const newData = selectedArray.find(
              (s) => s === filteredOptions[focusedOptionIndex].id,
            )
              ? selectedArray.filter(
                  (s) => s !== filteredOptions[focusedOptionIndex].id,
                )
              : [
                  ...(selectedArray as string[]),
                  filteredOptions[focusedOptionIndex].id,
                ];
            setSelected(newData);
            onChange(newData as string & string[]);
          }
          break;
        case 'Escape':
          event.stopPropagation();
          setShow(false);
          break;
        default:
          break;
      }
    },
    [
      filteredOptions,
      focusedOptionIndex,
      handleSelect,
      multiple,
      onChange,
      selected,
    ],
  );

  const cls = React.useMemo(
    () =>
      classNames(styles['label-wrapper'], className, {
        disabled,
        error: !!errorText,
        'size-sm': size === 'sm',
      }),
    [className, disabled, errorText, size],
  );

  return (
    <>
      <div className={cls} style={style}>
        {label && (
          <div className="d-flex gap-1 text-items-center mb-2">
            <Text as="label" htmlFor={inputId} weight="bold">
              {label}
            </Text>
            {tooltip && <CustomTooltipInfo info={tooltip} />}
          </div>
        )}

        {description && (
          <Text
            as="span"
            className="d-block mb-2"
            size="sm2"
            color="blackLight"
          >
            {description}
          </Text>
        )}

        <div className="d-flex flex-justify-between align-items-center gap-2">
          {prepend}
          <div className="position-relative flex-1">
            <input
              id={inputId}
              name={name}
              className={classNames({
                prepended: !!prepend,
                appended: !!append,
                error: !!errorText,
                open: show,
                [`overlay-${overlayPlacement}`]: !!overlayPlacement && show,
              })}
              ref={setTarget}
              readOnly
              value={displayValue || ''}
              disabled={disabled}
              onClick={handleOpen}
              onBlur={handleOnBlur}
              onKeyDown={handleKeyDown}
              {...inputProps}
            />
            <Icon
              className={classNames(styles.icon, {
                disabled,
              })}
              size="sm"
              icon={show ? faChevronUp : faChevronDown}
            />
          </div>
          {append}
        </div>

        {helperText && !errorText && (
          <Text as="span" className="mt-2" size="sm1">
            {helperText}
          </Text>
        )}
        {errorText && (
          <Text as="span" className="mt-2" color="red400" size="sm1">
            {errorText}
          </Text>
        )}
      </div>

      <Overlay
        className={classNames(styles.overlay, 'tooltip')}
        target={target}
        show={show}
        onHide={() => {
          handleOverlayHide();
          setShow(false);
          setSearch('');
          onSearch?.('');
        }}
        placement="bottom-start"
        matchReferenceSize
        offset={0}
        onUpdatePlacement={setOverlayPlacement}
      >
        <div
          className={classNames({
            error: !!errorText,
          })}
          ref={viewportRef}
        >
          {withSearch && (
            <div className={styles['searchbar-wrapper']}>
              <SearchBar
                className={styles.searchbar}
                placeholder={searchPlaceholder}
                value={controlledSearch || search}
                size="sm"
                onChange={(e) => {
                  if (!controlledSearch) {
                    setSearch(e.target.value);
                  }
                  onSearch?.(e.target.value);
                }}
                showIcon={false}
                ref={searchBarRef}
              />
            </div>
          )}

          <div
            role="listbox"
            className={classNames('px-3 pb-3', {
              'pt-0': withSearch,
              'pt-3': !withSearch,
            })}
          >
            {/* Render a radio option menu */}
            {loading && <Spinner size="sm" />}

            {!loading && withSearch && filteredOptions.length === 0 && (
              <Text size="sm2" color="blackLight">
                Nenhum dado encontrado
              </Text>
            )}

            {!loading && (
              <>
                {multiple && (
                  <ViewportList
                    viewportRef={viewportRef}
                    items={filteredOptions}
                  >
                    {(opt, idx, arr) => (
                      <Checkbox
                        data-option-index={idx}
                        key={opt.key ?? opt.id}
                        className={classNames(styles.checkbox, {
                          'bg-grayLight3 rounded-md':
                            focusedOptionIndex === idx,
                          last: idx === arr.length - 1,
                        })}
                        name={`${name}-${idx}`}
                        label={opt.label}
                        value={opt.id}
                        description={opt.description}
                        checked={selected?.includes(opt.id)}
                        onChange={handleChange}
                        ellipsize={false}
                        disabled={opt.disabled}
                      />
                    )}
                  </ViewportList>
                )}

                {!multiple && (
                  <ViewportList
                    viewportRef={viewportRef}
                    items={filteredOptions}
                  >
                    {(opt, idx) => (
                      <SelectItem
                        className={classNames('px-2', {
                          'bg-grayLight3 rounded-md':
                            focusedOptionIndex === idx,
                        })}
                        data-option-index={idx}
                        key={opt.key ?? opt.id}
                        description={opt.description}
                        onClick={() => !opt.disabled && handleSelect(opt.id)}
                        ellipsize={false}
                        disabled={opt.disabled}
                      >
                        {opt.label}
                      </SelectItem>
                    )}
                  </ViewportList>
                )}
              </>
            )}
          </div>
        </div>
      </Overlay>
    </>
  );
};

export default Select;
